import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { EnvelopeSimple, X } from "@phosphor-icons/react";

/**
 * Slim banner shown once at the top of the app for users whose
 * `email_verified` is false. Offers a one-tap resend and a dismiss (session-only).
 */
export default function VerifyEmailBanner() {
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState(() =>
    typeof window !== "undefined" && sessionStorage.getItem("dismiss_verify_banner") === "1"
  );
  const [busy, setBusy] = useState(false);
  if (!user || user.email_verified || dismissed) return null;

  const resend = async () => {
    setBusy(true);
    try {
      await api.post("/auth/resend-verification");
      toast.success(`Verification link sent to ${user.email}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not resend link");
    } finally { setBusy(false); }
  };

  const dismiss = (e) => {
    if (e && typeof e.stopPropagation === "function") e.stopPropagation();
    setDismissed(true);
    try { sessionStorage.setItem("dismiss_verify_banner", "1"); }
    catch (err) { console.warn("sessionStorage unavailable", err); }
  };

  return (
    <div
      data-testid="verify-email-banner"
      className="relative z-[60] bg-amber-50 border-b border-amber-200 px-4 py-2.5 flex items-center justify-between gap-3 text-sm"
    >
      <div className="flex items-center gap-2 text-amber-900">
        <EnvelopeSimple size={16} weight="fill" className="text-amber-600" />
        <span>
          <b>Confirm your email</b> to unlock {user.role === "staff" ? "booking shifts" : user.role === "company" ? "posting jobs" : "the platform"}.
          We sent a link to <b>{user.email}</b>.
        </span>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button
          onClick={resend}
          disabled={busy}
          data-testid="verify-banner-resend"
          className="font-semibold text-amber-900 underline hover:no-underline disabled:opacity-50"
        >
          {busy ? "Sending…" : "Resend"}
        </button>
        <button
          onClick={dismiss}
          data-testid="verify-banner-dismiss"
          className="text-amber-700 hover:text-amber-900 p-1"
          aria-label="Dismiss"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}
